import type {
  CollectionActions,
  CollectionStateSlice,
} from '@freelancer/datastore/core';
import {
  deepSpread,
  mergeDocuments,
  mergeWebsocketDocuments,
  pluckDocumentFromRawStoreCollectionState,
  transformIntoDocuments,
} from '@freelancer/datastore/core';
import { toNumber } from '@freelancer/utils';
import { ContestHandoverUpdateAction } from './contest-handovers.backend-model';
import { transformContestHandovers } from './contest-handovers.transformers';
import type { ContestHandoversCollection } from './contest-handovers.types';

export function contestHandoversReducer(
  state: CollectionStateSlice<ContestHandoversCollection> = {},
  action: CollectionActions<ContestHandoversCollection>,
): CollectionStateSlice<ContestHandoversCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'contestHandovers') {
        const { result, ref, order } = action.payload;
        return mergeDocuments<ContestHandoversCollection>(
          state,
          transformIntoDocuments(result.handovers, transformContestHandovers),
          order,
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'contestHandovers') {
        const { delta, originalDocument, result, ref } = action.payload;
        switch (delta.action) {
          case ContestHandoverUpdateAction.SUBMIT:
            return mergeWebsocketDocuments<ContestHandoversCollection>(
              state,
              transformIntoDocuments([result], transformContestHandovers),
              ref,
            );
          // Sign agreement action doesn't return anything, so we need to update the handover manually
          case ContestHandoverUpdateAction.SIGN_AGREEMENT: {
            const handover = pluckDocumentFromRawStoreCollectionState(
              state,
              ref.path,
              originalDocument.id.toString(),
            );

            if (!handover) {
              throw new Error('Handover was not found in the store.');
            }

            return mergeWebsocketDocuments<ContestHandoversCollection>(
              state,
              [
                deepSpread(
                  handover,
                  toNumber(ref.path.authUid) === handover.buyerId
                    ? { buyerSignedContract: true, buyerSignedDate: Date.now() }
                    : {
                        sellerSignedContract: true,
                        sellerSignedDate: Date.now(),
                      },
                ),
              ],
              ref,
            );
          }
          // Release prize action doesn't return anything, so we need to update the handover manually
          case ContestHandoverUpdateAction.RELEASE_PRIZE: {
            const handover = pluckDocumentFromRawStoreCollectionState(
              state,
              ref.path,
              originalDocument.id.toString(),
            );

            if (!handover) {
              throw new Error('Handover was not found in the store.');
            }

            return mergeWebsocketDocuments<ContestHandoversCollection>(
              state,
              [
                deepSpread(handover, {
                  buyerConfirmed: true,
                  buyerConfirmedDate: Date.now(),
                }),
              ],
              ref,
            );
          }
          default:
            return state;
        }
      }
      return state;
    }

    default:
      return state;
  }
}
